/**
 * Sticky Add to Cart Bar — JavaScript
 * Native replacement for SATCB third-party app
 *
 * Features:
 * - IntersectionObserver to show/hide bar when main ATC button leaves viewport
 * - Clones variant selectors from main form with two-way sync
 * - Price syncing via MutationObserver on #productPrice / #comparePrice
 * - Title updates reflecting selected variant options
 * - Judge.me review badge cloning
 * - AJAX add-to-cart with "Product Added!" confirmation
 */
(function () {
  'use strict';

  // --- Helpers ---
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return (ctx || document).querySelectorAll(sel); }

  function init() {
    var bar = qs('#sticky-atc-bar');
    if (!bar) return;

    var mainForm = qs('#addToCartForm');
    var mainAtcBtn = qs('#addToCart');
    if (!mainForm || !mainAtcBtn) return;

    // ─── INTERSECTION OBSERVER — show/hide bar ─────────────────────────
    // Only show the sticky bar when the main ATC button has been scrolled
    // PAST (above the viewport), not when it's still below the viewport.
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          // Button is visible — hide sticky bar
          bar.style.transform = 'translateY(calc(100% + 80px))';
        } else if (entry.boundingClientRect.top < 0) {
          // Button is above viewport (scrolled past) — show sticky bar
          bar.style.transform = 'translateY(0)';
        } else {
          // Button is below viewport (not reached yet) — keep hidden
          bar.style.transform = 'translateY(calc(100% + 80px))';
        }
      });
    }, { threshold: 0 });

    observer.observe(mainAtcBtn);

    // ─── CLONE VARIANT SELECTORS ────────────────────────────────────────
    var variantsContainer = qs('#sticky-atc-variants');
    var selectorWrappers = qsa('.selector-wrapper', mainForm);
    var qtySelect = qs('#productSelect-option-3');

    // Track original selects for title updates
    var originalSelects = [];

    selectorWrappers.forEach(function (wrapper) {
      // Skip hidden wrappers (e.g. default single-variant products)
      if (wrapper.style.display === 'none' || wrapper.offsetHeight === 0) return;

      var originalSelect = qs('select.single-option-selector', wrapper);
      if (!originalSelect) return;

      originalSelects.push(originalSelect);

      var div = document.createElement('div');
      div.className = 'sticky-atc-variant-item';

      // Clone label if present
      var label = qs('label', wrapper);
      if (label) {
        var labelClone = document.createElement('label');
        labelClone.textContent = label.textContent;
        labelClone.className = 'sticky-atc-variant-label';
        div.appendChild(labelClone);
      }

      // Clone the select
      var selectClone = originalSelect.cloneNode(true);
      selectClone.id = 'sticky-' + originalSelect.id;
      selectClone.className = 'sticky-atc-select';
      selectClone.removeAttribute('data-option');
      div.appendChild(selectClone);

      variantsContainer.appendChild(div);

      // Two-way sync: sticky → original
      selectClone.addEventListener('change', function () {
        originalSelect.value = selectClone.value;
        triggerChange(originalSelect);
      });

      // Two-way sync: original → sticky
      originalSelect.addEventListener('change', function () {
        selectClone.value = originalSelect.value;
      });
    });

    // Clone Qty selector
    if (qtySelect) {
      var qtyDiv = document.createElement('div');
      qtyDiv.className = 'sticky-atc-variant-item sticky-atc-qty-item';

      var qtyLabel = document.createElement('label');
      qtyLabel.textContent = 'Qty';
      qtyLabel.className = 'sticky-atc-variant-label';
      qtyDiv.appendChild(qtyLabel);

      var qtyClone = qtySelect.cloneNode(true);
      qtyClone.id = 'sticky-productSelect-option-3';
      qtyClone.className = 'sticky-atc-select';
      qtyClone.removeAttribute('data-option');
      qtyDiv.appendChild(qtyClone);

      variantsContainer.appendChild(qtyDiv);

      // Two-way sync
      qtyClone.addEventListener('change', function () {
        qtySelect.value = qtyClone.value;
        triggerChange(qtySelect);
      });

      qtySelect.addEventListener('change', function () {
        qtyClone.value = qtySelect.value;
      });
    }

    /**
     * Trigger native + jQuery change event on an element.
     * The theme uses jQuery change handlers extensively.
     */
    function triggerChange(el) {
      var evt = document.createEvent('HTMLEvents');
      evt.initEvent('change', true, false);
      el.dispatchEvent(evt);
      if (window.jQuery) {
        window.jQuery(el).trigger('change');
      }
    }

    // ─── PRICE SYNCING ──────────────────────────────────────────────────
    var mainPrice = qs('#productPrice');
    var mainCompare = qs('#comparePrice');
    var stickyPrice = qs('#sticky-atc-current-price');
    var stickyCompare = qs('#sticky-atc-compare-price');

    function syncPrice() {
      if (mainCompare && stickyCompare) {
        var isHidden = mainCompare.style.display === 'none' ||
                       window.getComputedStyle(mainCompare).display === 'none';
        if (isHidden) {
          stickyCompare.style.display = 'none';
        } else {
          // Extract just the dollar amount from the save text (e.g. "Save $ 5.52" → "$5.52")
          var saveText = mainCompare.textContent.trim();
          var amountMatch = saveText.match(/\$\s*[\d,.]+/);
          var amount = amountMatch ? amountMatch[0].replace(/\$\s+/, '$') : saveText;
          stickyCompare.innerHTML = '<div style="font-size:9px;line-height:1;">Save</div><div style="font-size:12px;line-height:1.2;">' + amount + '</div>';
          stickyCompare.style.display = 'block';
        }
      }
    }

    // Observe price element for content changes
    if (mainPrice) {
      new MutationObserver(syncPrice).observe(mainPrice, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    // Observe compare price for content + visibility changes
    if (mainCompare) {
      new MutationObserver(syncPrice).observe(mainCompare, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      });
    }

    // Initial sync
    syncPrice();

    // ─── TITLE — product name only, no variant info ───────────────────
    var titleEl = qs('#sticky-atc-title');
    var baseTitle = titleEl.getAttribute('data-base-title');
    titleEl.textContent = baseTitle;

    // Scroll to top when title link is clicked
    titleEl.addEventListener('click', function (e) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    // ─── JUDGE.ME REVIEW BADGE CLONING ──────────────────────────────────
    var reviewsContainer = qs('#sticky-atc-reviews');

    function cloneReviewBadge() {
      if (reviewsContainer.hasChildNodes()) return;
      var badge = qs('.jdgm-prev-badge');
      if (badge) {
        reviewsContainer.appendChild(badge.cloneNode(true));
      }
    }

    // Judge.me loads async; retry a few times
    setTimeout(cloneReviewBadge, 2000);
    setTimeout(cloneReviewBadge, 4000);
    setTimeout(cloneReviewBadge, 6000);

    // ─── BUTTON PRICE LABEL ────────────────────────────────────────────
    // Show price inside the ATC button: "Add to Cart - $xx.xx"
    var stickyBtn = qs('#sticky-atc-btn');
    var btnTextSpan = qs('#sticky-atc-btn span');

    function syncButtonPrice() {
      if (!mainPrice || !btnTextSpan) return;
      var priceText = mainPrice.textContent.trim().replace(/\$\s+/g, '$');
      btnTextSpan.textContent = priceText
        ? 'Add to Cart - ' + priceText
        : 'Add to Cart';
    }

    // Sync on price changes via a second observer
    if (mainPrice) {
      new MutationObserver(syncButtonPrice).observe(mainPrice, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
    syncButtonPrice();

    // ─── ADD TO CART (AJAX) ─────────────────────────────────────────────
    var confirmation = qs('#sticky-atc-confirmation');
    var masterSelect = qs('#productSelect');

    stickyBtn.addEventListener('click', function () {
      if (stickyBtn.disabled) return;

      var variantId = masterSelect ? masterSelect.value : null;
      var qty = qtySelect ? qtySelect.value : 1;

      if (!variantId) return;

      // Build payload
      var cartData = {
        id: parseInt(variantId, 10),
        quantity: parseInt(qty, 10)
      };

      // Track that this item was added from the sticky bar.
      // Vehicle tagging (__vehicle property) is injected globally by
      // snippets/vehicle-tracking.liquid — no per-handler logic needed.
      cartData.properties = { '__added_from': 'sticky_bar' };

      // Disable button during request
      stickyBtn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartData)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(function () {
          // Klaviyo tracking
          if (window._learnq && window.item) {
            window._learnq.push(['track', 'Added to Cart', window.item]);
          }

          // Redirect to cart page (matches main ATC button behavior)
          window.location.href = '/cart';
        })
        .catch(function (err) {
          console.error('Sticky ATC error:', err);
          stickyBtn.disabled = false;
          // Show a brief error state
          var origHTML = stickyBtn.innerHTML;
          stickyBtn.innerHTML = '<span>Error — try again</span>';
          setTimeout(function () {
            stickyBtn.innerHTML = origHTML;
          }, 2000);
        });
    });
  }

  // ─── BOOTSTRAP ──────────────────────────────────────────────────────
  // Wait for DOM ready + 500ms for Shopify.OptionSelectors to initialize
  function bootstrap() { setTimeout(init, 500); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
